import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { Td } from "../../features/web3/forex/components/table";
import { formatDateTime } from "../../lib/format";
import { useCan, useForexAdmin } from "./api";
import {
  AccountLink,
  DealCard,
  Drawer,
  ListView,
  Money,
  QueryState,
  Stat,
  UserLink,
  mono,
  muted,
  orDash,
  useOpen,
  useTab,
} from "./parts";

const TABS = [
  { value: "all", label: "All transfers" },
  { value: "stranded", label: "Stranded" },
];

const STATE_TONE = {
  COMPLETED: "text-emerald-400",
  FAILED: "text-red-400",
  PENDING: "text-amber-400",
};

// 017's labels; a row on the stranded list reads Stranded.
const STATE_LABEL = { PENDING: "Pending", COMPLETED: "Completed", FAILED: "Failed" };

const COLUMNS = [
  { label: "Created" },
  { label: "Ref" },
  { label: "State" },
  { label: "Username" },
  { label: "From login" },
  { label: "To login" },
  { label: "Amount", align: "right" },
  { label: "Debit" },
  { label: "Credit" },
  { label: "Refund" },
];

const Login = ({ id, login }) => <AccountLink id={id}>{login}</AccountLink>;

// A transfer between two of one user's own MT5 accounts: debit on the source,
// credit on the destination, and a refund back to the source if the credit failed.
// onOpen is null when the row opens nothing (no transfers.read).
export const transferRow = (onOpen, stranded) => (r) => (
  <tr key={r.id} onClick={(e) => onOpen && !e.target.closest("a") && onOpen(r.id)} className={onOpen ? "cursor-pointer hover:bg-white/[0.02]" : undefined}>
    <Td className={muted}>{formatDateTime(r.createdAt)}</Td>
    <Td className={mono}>{r.ref}</Td>
    <Td className={`text-xs font-medium ${STATE_TONE[r.state]}`}>{stranded ? "Stranded" : STATE_LABEL[r.state] ?? r.state}</Td>
    <Td className={mono}><UserLink username={r.username} /></Td>
    <Td className={mono}><Login id={r.sourceAccountId} login={r.sourceLogin} /></Td>
    <Td className={mono}><Login id={r.destinationAccountId} login={r.destinationLogin} /></Td>
    <Td align="right"><Money value={r.amount} /></Td>
    <Td className={mono}>{orDash(r.debitTicket)}</Td>
    <Td className={mono}>{orDash(r.creditTicket)}</Td>
    <Td className={mono}>{orDash(r.refundTicket)}</Td>
  </tr>
);

// Also opened from the user page, so it checks transfers.read itself (025).
export function TransferDrawer({ id, onClose }) {
  if (!useCan()("transfers.read")) id = null;
  const q = useForexAdmin(id ? `/transfers/admin/${id}` : null);
  return (
    <Drawer open={id} onClose={onClose} title="Transfer">
      <QueryState q={q}>
        {(t) => (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Stat label="State"><span className={STATE_TONE[t.state]}>{STATE_LABEL[t.state] ?? t.state}</span></Stat>
              <Stat label="Amount"><Money value={t.amount} /></Stat>
              <Stat label="From login"><Login id={t.sourceAccountId} login={t.sourceLogin} /></Stat>
              <Stat label="To login"><Login id={t.destinationAccountId} login={t.destinationLogin} /></Stat>
              {/* null = the account was removed since; its login still stands. */}
              <Stat label="From account id"><span className="break-all text-xs">{orDash(t.sourceAccountId)}</span></Stat>
              <Stat label="To account id"><span className="break-all text-xs">{orDash(t.destinationAccountId)}</span></Stat>
              <Stat label="Username"><UserLink username={t.username} /></Stat>
              <Stat label="Ref">{t.ref}</Stat>
              <Stat label="Id"><span className="break-all text-xs">{t.id}</span></Stat>
              <Stat label="Debit ticket">{orDash(t.debitTicket)}</Stat>
              <Stat label="Credit ticket">{orDash(t.creditTicket)}</Stat>
              <Stat label="Refund ticket">{orDash(t.refundTicket)}</Stat>
            </div>
            <p className="text-xs text-muted-foreground">Created {formatDateTime(t.createdAt)}</p>
            {t.deals ? (
              <div className="space-y-3">
                <DealCard label="Debit (source)" deal={t.deals.debit} />
                <DealCard label="Credit (destination)" deal={t.deals.credit} />
                <DealCard label="Refund (source)" deal={t.deals.refund} />
              </div>
            ) : (
              <p className="text-sm text-amber-400">Deals unavailable right now, try again.</p>
            )}
          </>
        )}
      </QueryState>
    </Drawer>
  );
}

const FIELDS = [
  { name: "state", label: "State", options: [
    { value: "", label: "All states" },
    { value: "PENDING", label: "Pending" },
    { value: "COMPLETED", label: "Completed" },
    { value: "FAILED", label: "Failed" },
  ] },
  { name: "username", label: "Username (exact)" },
  { name: "login", label: "MT5 login (either side)" },
  { name: "from", label: "Created from", type: "datetime-local" },
  { name: "to", label: "Created to", type: "datetime-local" },
];

export default function MtTransfers() {
  const [tab, setTab] = useTab("all");
  const [open, setOpen] = useOpen();
  const qc = useQueryClient();
  const fetching = useIsFetching({ queryKey: ["forex-admin"] }) > 0;
  const stranded = tab === "stranded";

  return (
    <QueueLayout
      eyebrow="Forex Admin"
      title="MT5"
      accent="transfers"
      description={
        stranded
          ? "Pending for over 5 minutes, oldest first. The money may have left the source and reached neither account: check both logins on the trading server."
          : "Money a user moved between two of their own MT5 accounts. Not the wallet transfers under Forex."
      }
      statusOptions={TABS}
      status={tab}
      onStatus={setTab}
      isFetching={fetching}
      onRefresh={() => qc.invalidateQueries({ queryKey: ["forex-admin"] })}
    >
      {stranded ? (
        <ListView key="stranded" path="/transfers/admin/stranded" empty={{}} fields={[]}
          columns={COLUMNS} row={transferRow(setOpen, true)} label="stranded" />
      ) : (
        <ListView key="all" path="/transfers/admin"
          empty={{ state: "", username: "", login: "", from: "", to: "" }} fields={FIELDS}
          columns={COLUMNS} row={transferRow(setOpen, false)} label="transfers" />
      )}
      <TransferDrawer id={open} onClose={() => setOpen(null)} />
    </QueueLayout>
  );
}
